<script setup>
import axios from '@/helpers/axios'
import Loader from '@/views/Loader.vue'
import Footer from '@/views/front-pages/front-page-footer.vue'
import Navbar from '@/views/front-pages/front-page-navbar.vue'
import AppSearchHeaderBg from '@images/pages/app-search-header-bg.png'
import moment from 'moment'
import { useTitle } from '@vueuse/core'

const { t } = useI18n()
const route = useRoute()

const uuid = route.params.uuid
const routeLastData = route.query?.last_data

const webcam = ref(null)
const images = ref([])
const selectedImage = ref(null)
const loading = ref(true)
const menu = ref(false)
const hasAppliedLastData = ref(false)
const selectedDate = ref(routeLastData && moment(routeLastData).isValid()
  ? moment(routeLastData).format('YYYY-MM-DD')
  : moment().format('YYYY-MM-DD'))

const pageTitle = useTitle('Kropie')

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const getData = () => {
  loading.value = true
  axios.get(`/landing/webcam/${uuid}`, {
    params: { date: selectedDate.value },
  }).then(response => {
    webcam.value = response.data
    images.value = response.data.images ?? []
    selectedImage.value = images.value.length > 0 ? images.value[0] : null

    if (webcam.value?.name) {
      pageTitle.value = `${webcam.value.name} - Kropie`
    }

    if (!hasAppliedLastData.value && response.data.last_data) {
      const latestDate = moment(response.data.last_data).format('YYYY-MM-DD')
      if (selectedDate.value !== latestDate) {
        selectedDate.value = latestDate
      }
      hasAppliedLastData.value = true
    }
  }).catch(error => {
    console.error(error)
    webcam.value = null
  }).finally(() => {
    loading.value = false
  })
}

getData()

watch(selectedDate, (newVal, oldVal) => {
  if (newVal === oldVal) return
  getData()
})

const formattedDate = date => {
  if (!date) return ''
  return moment(date).format('HH:mm')
}

const onDateSelect = value => {
  selectedDate.value = moment(value).format('YYYY-MM-DD')
  menu.value = false
}
</script>

<template>
  <div class="help-center-page">
    <Navbar />

    <VCard
      flat
      class="text-center search-header"
      :style="`background: url(${AppSearchHeaderBg});`"
    >
      <VCardText>
        <h2 class="text-h2 font-weight-medium py-10">
          {{ $t('webcams.title') }}
        </h2>

        <p class="text-disabled mb-6">
          {{ $t('webcams.webcams_description') }}
        </p>
      </VCardText>
    </VCard>

    <div class="py-10 bg-surface">
      <VContainer>
        <Loader v-if="loading" class="mt-4" />

        <div v-else>
          <div v-if="!webcam">
            <VCard class="text-center py-10">
              <VCardText>
                <VIcon icon="tabler-camera-off" size="48" color="grey" class="mb-3" />
                <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
                  {{ $t('webcams.webcam_not_found') }}
                </h4>
              </VCardText>
            </VCard>
          </div>

          <div v-else>
            <div class="page-header mb-4">
              <div class="d-flex flex-column flex-sm-row align-center justify-space-between gap-4">
                <div class="flex-grow-1 text-center text-sm-left">
                  <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
                    {{ webcam.name }}
                  </h2>
                </div>

                <div class="d-flex justify-end" style="min-width: 290px;">
                  <VMenu
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template #activator="{ props }">
                      <VTextField
                        v-model="selectedDate"
                        :label="$t('webcams.select_date')"
                        readonly
                        v-bind="props"
                        prepend-inner-icon="tabler-calendar"
                        style="max-width: 290px; width: 100%;"
                      />
                    </template>

                    <VDatePicker
                      v-model="selectedDate"
                      hide-header
                      :max="moment().format('YYYY-MM-DD')"
                      @update:model-value="onDateSelect"
                    />
                  </VMenu>
                </div>
              </div>
            </div>

            <div v-if="images.length === 0">
              <div class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10">
                <VIcon icon="tabler-photo-off" size="48" color="grey" class="mb-3" />
                <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
                  {{ $t('webcams.no_images') }}
                </h4>
              </div>
            </div>

            <div v-else>
              <VCard
                v-if="selectedImage"
                style="border-radius: 16px; position: relative; overflow: hidden;"
                elevation="2"
              >
                <VImg
                  :src="selectedImage.image_url"
                  alt="Webcam image"
                  cover
                  height="100%"
                  width="100%"
                />

                <div
                  class="pa-2"
                  style="
                    position: absolute;
                    top: 8px;
                    left: 8px;
                    background-color: rgba(0, 0, 0, 0.55);
                    color: white;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 1.3rem;
                    backdrop-filter: blur(4px);
                  "
                >
                  {{ formattedDate(selectedImage.captured_at) }}
                </div>
              </VCard>

              <VRow v-if="images.length" class="mt-4">
                <VCol
                  v-for="item in images"
                  :key="item.uuid"
                  cols="4"
                  sm="4"
                  md="3"
                  lg="2"
                >
                  <VCard
                    style="border-radius: 16px; position: relative; height: 120px; overflow: hidden;"
                    elevation="2"
                    class="cursor-pointer"
                    @click="selectedImage = item"
                  >
                    <VImg
                      :src="item.thumbnail_url"
                      alt="Webcam image"
                      cover
                      height="100%"
                      width="100%"
                    />

                    <div
                      class="pa-2"
                      style="
                        position: absolute;
                        top: 8px;
                        left: 8px;
                        background-color: rgba(0, 0, 0, 0.55);
                        color: white;
                        border-radius: 8px;
                        font-weight: 700;
                        font-size: 0.9rem;
                        backdrop-filter: blur(4px);
                      "
                    >
                      {{ formattedDate(item.captured_at) }}
                    </div>

                    <VIcon
                      v-if="selectedImage && selectedImage.uuid === item.uuid"
                      icon="tabler-check"
                      size="35"
                      color="primary"
                      style="
                        position: absolute;
                        bottom: 6px;
                        right: 6px;
                        border-radius: 50%;
                        padding: 2px;
                      "
                    />
                  </VCard>
                </VCol>
              </VRow>
            </div>
          </div>
        </div>
      </VContainer>
    </div>

    <Footer />
  </div>
</template>

<style lang="scss">
.help-center-page {
  .search-header {
    background-size: cover !important;
    padding-block-start: 7rem !important;
  }
}

@media (max-width: 960px) and (min-width: 600px) {
  .help-center-page {
    .v-container {
      padding-inline: 2rem !important;
    }

    .search-header {
      padding: 5rem !important;
    }
  }
}

@media (max-width: 599px) {
  .help-center-page {
    .search-header {
      padding-block-end: 2rem !important;
      padding-block-start: 4rem !important;
      padding-inline: 2rem !important;
    }
  }
}
</style>
